<template>
    <div class="map-loading-overlay" :class="{ active: isLoading }">
        <div class="map-loading-spinner"></div>
    </div>
    <div class="map-fade-overlay" :class="{ active: isFading }"></div>

    <!-- Google Maps Web Components 方式 -->
    <gmp-map id="map" ref="mapComponent" :map-id="mapId" :zoom="defaultZoom" :center="mapCenter" 
        :style="mapStyle"
        :gesture-handling="'greedy'" :disable-default-ui="true" :clickable-icons="false" :zoom-control="false"
        :map-type-control="false" :scale-control="false" :street-view-control="false" :rotate-control="false"
        :fullscreen-control="false" :keyboard-shortcuts="false" @gmp-tilesloading="onTilesLoading"
        @gmp-tilesloaded="onTilesLoaded" @gmp-idle="onMapIdle" @gmp-mousemove="updateMousePosition"
        @gmp-zoom-changed="onZoomChanged" @gmp-ready="onMapReady">
        <!-- 動態創建的進階標記將插入到這裡 -->
        <gmp-advanced-marker v-for="field in visibleFields" :key="field.id"
            :position="{ lat: Number(field.lat), lng: Number(field.lng) }" :title="field.name" :gmp-clickable="true"
            @gmp-click="() => onMarkerClick(field)">
            <!-- 自定義 HTML 標記內容 -->
            <div class="custom-advanced-marker" :data-field-id="field.id" :class="getMarkerClass(field)">
                <!-- 名稱標籤（懸停顯示） -->
                <div class="name-label">{{ field.name }}</div>
                <!-- 狀態警告圖示 -->
                <div v-if="field.status === 'error'" class="error-badge">!</div>
                <!-- 主體圓圈 -->
                <div class="marker-body">{{ firstLetter(field.name) }}</div>
                <!-- 指向箭頭 -->
                <div class="marker-tail"></div>
            </div>
        </gmp-advanced-marker>
    </gmp-map>

    <div id="mouse-position">
        <span @click="copyMousePosition">
            {{ mousePosition.lat }}, {{ mousePosition.lng }}
            <Icon type="md-search" style="margin-left: 10px;" /> {{ currentZoom }}
        </span>
        <label class="animation-toggle">
            <input type="checkbox" v-model="localEnableAnimation" :disabled="view.isMobile">
            動畫
        </label>
    </div>

</template>

<script>
import { db } from '@shared/config/firebaseConfig'
import { collection, getDocs } from 'firebase/firestore'
import { eventBus } from '@shared/stores/globalState'
import {
    calculateDistance,
    calculateAnimationDuration,
    calculateIntermediateZoom,
    easeInOutCubic,
    calculateIntermediatePoint,
    getMapCenter
} from '@shared/utils/mapUtils'

export default {
    name: 'comMap',
    props: {
        fields: {
            type: Array,
            default: () => []
        },
        dataField: {
            type: Array,
            default: () => []
        },
        enableAnimation: {
            type: Boolean,
            default: true
        },
    },
    data() {
        return {
            mapId: "3d05c09a9a359b52", // 進階標記需要有效的 mapId
            defaultZoom: 8,
            map: null,
            mapStore: null,
            mapTiles: [],
            mapTilesCache: new Map(),
            imageCache: new Map(), // 圖片本地快取
            tileLoadingCount: 0,
            defaultLocation: { lat: 23.58, lng: 120.28 },
            mousePosition: { lat: 0, lng: 0 },
            mapInitialized: false,
            mapOperationInProgress: false,
            mapOperationTimeout: null,
            isMapAnimating: false,
            layerControl: null,
            fieldMapTileGroup: null,
            markerGroup: null,
            allMarkers: {},
            mapAnimationTimeout: null,
            currentMapAnimation: null,
            currentMapType: localStorage.getItem('mapType') || 'satellite',
            mapLayers: null,
            iconCache: {},
            markers: {},
            localEnableAnimation: localStorage.getItem('mapAnimationEnabled') === 'true' || true,
            isLoading: true,
            isFading: false,
            isAddingIcons: false,
            addIconsTimer: null,
            lastIconsTimestamp: 0,
            isFlyingOut: false,
            flyOutTimer: null,
            lastFlyOutTimestamp: 0,

            // UI 移除防重複機制
            lastUIRemovalTime: 0,
            loadingMessage: '正在載入地圖...',
            selectedField: null,
            uiRemovalInterval: null,
            currentCenter: { lat: 0, lng: 0 },
            centerInterval: null,
            mapTypeReady: false, // 地圖類型是否已設置完成
        }
    },
    computed: {
        isFieldSelected() {
            // 檢查是否選中了特定案場（dataField 只包含一個案場）
            return this.dataField && this.dataField.length === 1;
        },

        currentZoom() {
            return this.map?.zoom || this.defaultZoom;
        },

        // 地圖中心點（固定使用默認位置，不再隨篩選自動移動）
        mapCenter() {
            return this.defaultLocation;
        },

        mapStyle() {
            const baseStyle = {
                height: this.fieldScope.isMobile ? 'calc(100% + 20px)' : 'calc(100% + 25px)',
                width: this.fieldScope.isMobile ? '100%' : 'calc(100% + 370px)',
                position: 'fixed',
                top: 0,
                bottom: 0,
                left: this.fieldScope.isMobile ? 0 : '-220px',
                right: '-220px',
                visibility: this.mapTypeReady ? 'visible' : 'hidden',
                opacity: this.mapTypeReady ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out',
                zIndex: this.fieldScope.isMobile ? 5 : 6
            };

            return baseStyle;
        },

        // 根據裝置類型決定是否啟用動畫
        shouldEnableAnimation() {
            // 如果是手機版，直接返回 false
            if (this.fieldScope.isMobile) {
                return false;
            }
            // 使用本地狀態控制動畫
            return this.localEnableAnimation;
        },

        visibleFields() {
            const filtered = this.dataField.filter(field => {
                if (!field) return false;

                // 支援多種經緯度欄位格式
                const lat = parseFloat(field.lat || field.latitude);
                const lng = parseFloat(field.lng || field.longitude);

                if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
                    return false;
                }

                return true;
            }).map(field => {
                // 確保返回的 field 對象包含正確的數字格式經緯度
                const processedField = {
                    ...field,
                    lat: parseFloat(field.lat || field.latitude),
                    lng: parseFloat(field.lng || field.longitude)
                };

                return processedField;
            });
            return filtered;
        }
    },
    watch: {
        // dataField 變化時不再自動移動地圖，避免篩選切換時地圖亂跳
        dataField: {
            handler(newData, oldData) {
                // 僅更新標記，不移動地圖視角
            }
        },

        localEnableAnimation: {
            handler(newValue) {
                localStorage.setItem('mapAnimationEnabled', newValue);
            }
        },

    },
    async mounted() {
        // 從 localStorage 讀取保存的地圖類型（如果有的話）
        const savedMapType = localStorage.getItem('mapType');
        if (savedMapType) {
            this.currentMapType = savedMapType;
        } else {
            this.currentMapType = 'satellite';
        }

        // 監聽 Google Maps API 載入完成事件
        window.addEventListener('google-maps-loaded', this.onGoogleMapsLoaded);

        // 如果 API 已經載入，直接初始化
        if (window.google && window.google.maps) {
            await this.onGoogleMapsLoaded();
        }

        // 監聽來自 SystemSettings 的地圖類型切換請求
        eventBus.on('ui:map-type-change', this.handleMapTypeChangeRequest);
        eventBus.on('ui:map-type-request', this.handleMapTypeRequest);

        // 發送當前地圖類型
        this.broadcastMapType();

        // 將組件實例暴露到全局，方便調試
        window.mapComponent = this;

        // 暴露快取管理方法到全局，方便調試
        window.mapCache = {
            stats: () => this.getCacheStats(),
            clear: () => this.clearImageCache(),
            reload: () => this.reloadMapTiles(),
            size: () => this.imageCache.size
        };

        // 備用清除載入狀態機制
        setTimeout(() => {
            if (this.isLoading) {
                this.isLoading = false;
            }
        }, 3000);

        // 定期檢查地圖是否已經載入完成
        const checkMapLoaded = () => {
            const mapElement = this.$refs.mapComponent;
            if (mapElement && mapElement.innerMap && this.isLoading) {
                this.isLoading = false;

                // 確保地圖類型正確設置（使用保存的類型）
                this.ensureMapType();
            } else if (this.isLoading) {
                setTimeout(checkMapLoaded, 1000);
            }
        };

        setTimeout(checkMapLoaded, 1000);

        this.centerInterval = setInterval(() => {
            const mapElement = this.$refs.mapComponent;
            this.currentCenter = getMapCenter(mapElement?.innerMap);
        }, 100);
    },

    // 添加 activated 鉤子（用於 keep-alive 組件）
    activated() {
        if (!this.mapInitialized) {
            this.$nextTick(async () => {
                await this.loadGoogleMapsWebComponents();
            });
        }
    },

    // 添加 deactivated 鉤子（用於 keep-alive 組件）
    deactivated() {
        // 清理定時器
        if (this.mapOperationTimeout) {
            clearTimeout(this.mapOperationTimeout);
            this.mapOperationTimeout = null;
        }

        if (this.mapAnimationTimeout) {
            clearTimeout(this.mapAnimationTimeout);
            this.mapAnimationTimeout = null;
        }

        if (this.addIconsTimer) {
            clearTimeout(this.addIconsTimer);
            this.addIconsTimer = null;
        }

        if (this.flyOutTimer) {
            clearTimeout(this.flyOutTimer);
            this.flyOutTimer = null;
        }

        if (this.centerInterval) {
            clearInterval(this.centerInterval);
            this.centerInterval = null;
        }


    },

    beforeUnmount() {
        // 移除 eventBus 監聽器
        eventBus.off('ui:map-type-change', this.handleMapTypeChangeRequest);
        eventBus.off('ui:map-type-request', this.handleMapTypeRequest);

        // 清除動畫
        if (this.currentMapAnimation) {
            cancelAnimationFrame(this.currentMapAnimation);
            this.currentMapAnimation = null;
        }

        // 清除計時器
        if (this.flyOutTimer) {
            clearTimeout(this.flyOutTimer);
            this.flyOutTimer = null;
        }

        if (this.uiRemovalInterval) {
            clearInterval(this.uiRemovalInterval);
            this.uiRemovalInterval = null;
        }

        if (this.centerInterval) {
            clearInterval(this.centerInterval);
            this.centerInterval = null;
        }

        // 清除圖片快取
        this.clearImageCache();
    },

    methods: {
        getMarkerClass(field) {
            if (field.status === 'error') return 'status-error'
            if (field.risk_level === 'high') return 'risk-high'
            if (field.risk_level === 'medium') return 'risk-medium'
            if (field.risk_level === 'low') return 'risk-low'
            return ''
        },
        firstLetter(name) {
            const nameArray = name.split('-');
            if (nameArray.length > 1) {
                return nameArray[1].charAt(0).toUpperCase();
            }
            return name.charAt(0).toUpperCase();
        },
        // 載入 Google Maps Web Components
        async loadGoogleMapsWebComponents() {
            try {
                // 確保 Google Maps JavaScript API 已載入
                if (!window.google || !window.google.maps) {
                    console.error('Google Maps JavaScript API 未載入');
                    return;
                }

                // 等待 DOM 更新
                await this.$nextTick();

                // 等待地圖元素存在
                const mapElement = this.$refs.mapComponent;
                if (!mapElement) {
                    console.error('❌ 找不到地圖元素');
                    return;
                }

                // 等待 Web Component 完全初始化，使用更寬鬆的條件
                await new Promise((resolve) => {
                    const timeout = setTimeout(() => {
                        resolve();
                    }, 5000);

                    const checkInitialized = () => {
                        if (mapElement.innerMap || mapElement.map) {
                            clearTimeout(timeout);
                            this.map = mapElement.innerMap || mapElement.map;
                            resolve();
                        } else {
                            setTimeout(checkInitialized, 100);
                        }
                    };

                    checkInitialized();
                });

                this.mapInitialized = true;

                // 立即清除載入狀態
                this.isLoading = false;

                // 顯示地圖
                this.mapTypeReady = true;

            } catch (error) {
                console.error('❌ Web Components 地圖載入失敗:', error);
                this.isLoading = false;
            }
        },

        // 確保地圖顯示為衛星模式（保留以向後兼容）
        ensureSatelliteMode() {
            this.currentMapType = 'satellite';
            localStorage.setItem('mapType', 'satellite');
            this.ensureMapType();
        },
        // 確保地圖類型正確設置（使用 currentMapType）
        ensureMapType() {
            // 如果是 Windy 類型，不處理（由 WindyMap 組件處理）
            if (['wind', 'temp', 'pressure'].includes(this.currentMapType)) {
                this.$nextTick(() => {
                    this.mapTypeReady = true;
                });
                return;
            }

            this.$nextTick(() => {
                const mapElement = this.$refs.mapComponent;
                if (mapElement && mapElement.innerMap) {
                    try {
                        // 使用標準地圖類型常數
                        const mapTypes = {
                            satellite: window.google.maps.MapTypeId.SATELLITE,
                            roadmap: window.google.maps.MapTypeId.ROADMAP,
                            hybrid: window.google.maps.MapTypeId.HYBRID,
                            terrain: window.google.maps.MapTypeId.TERRAIN
                        };

                        const mapTypeId = mapTypes[this.currentMapType] || window.google.maps.MapTypeId.SATELLITE;
                        mapElement.innerMap.setMapTypeId(mapTypeId);
                        
                        // 等待一個短暫的時間確保地圖類型已應用，然後顯示地圖
                        setTimeout(() => {
                            this.mapTypeReady = true;
                        }, 100);
                    } catch (error) {
                        console.warn('⚠️ 設置地圖類型時遇到錯誤:', error);
                        // 即使出錯也顯示地圖，避免永久隱藏
                        this.mapTypeReady = true;
                    }
                } else {
                    // 如果地圖元素還不存在，稍後再試
                    setTimeout(() => {
                        this.ensureMapType();
                    }, 100);
                }
            });
        },

        // Google Maps API 載入完成處理
        async onGoogleMapsLoaded() {
            try {
                await this.loadGoogleMapsWebComponents();

                // 等待地圖元素準備好，然後立即設置地圖類型（避免先載入默認類型）
                const checkAndSetMapType = () => {
                    const mapElement = this.$refs.mapComponent;
                    if (mapElement && mapElement.innerMap) {
                        this.ensureMapType();
                    } else {
                        // 如果地圖還沒準備好，等待一下再試
                        setTimeout(checkAndSetMapType, 50);
                    }
                };
                checkAndSetMapType();

                // 延遲載入圖塊，確保地圖類型已設置
                setTimeout(async () => {
                    await this.loadMapTiles();
                }, 500);

                // 確保載入完成後清除載入狀態
                this.isLoading = false;

                // 強制更新一次，確保視圖更新
                this.$forceUpdate();
            } catch (error) {
                console.error('❌ 地圖組件初始化失敗:', error);
                // 即使失敗也要清除載入狀態，避免永久載入
                this.isLoading = false;
            }
        },

        // 地圖準備完成事件
        onMapReady() {
            // 設置地圖引用
            const mapElement = this.$refs.mapComponent;
            if (mapElement && mapElement.innerMap) {
                this.map = mapElement;

                // 設置初始化標記
                this.mapInitialized = true;
                this.isInitialized = true;
                this.showControls = true;

                // 立即設置地圖類型（使用保存的類型），設置完成後才會顯示地圖
                this.$nextTick(() => {
                    this.ensureMapType();
                });
            }

            // 清除載入狀態（但地圖仍隱藏直到類型設置完成）
            this.isLoading = false;
        },

        // 地圖圖塊開始載入
        onTilesLoading() {
            this.isLoading = true;
        },

        // 地圖圖塊載入完成
        onTilesLoaded() {
            this.isLoading = false;
        },

        // 地圖空閒狀態（所有操作完成）
        onMapIdle() {
            this.isLoading = false;

            // 取得地圖中心點
            const mapElement = this.$refs.mapComponent;
            if (mapElement && mapElement.innerMap) {
                this.currentCenter = getMapCenter(mapElement.innerMap);
            }

            // 確保載入狀態被清除
            setTimeout(() => {
                this.isLoading = false;
            }, 500);
        },

        // 滑鼠位置更新
        updateMousePosition(event) {
            if (event.detail && event.detail.latLng) {
                this.mousePosition = {
                    lat: event.detail.latLng.lat().toFixed(6),
                    lng: event.detail.latLng.lng().toFixed(6)
                };
            }
        },

        // 縮放變化處理
        onZoomChanged(event) {
            this.$forceUpdate();

            // 處理地圖圖塊可見性
            if (this.mapTiles.length > 0) {
                const zoom = event.detail?.zoom || this.currentZoom;
                this.mapTiles.forEach(overlay => {
                    if (zoom >= 17 && zoom <= 22) {
                        overlay.setOpacity(1); // 高縮放級別顯示圖塊
                    } else {
                        overlay.setOpacity(0); // 低縮放級別隱藏圖塊
                    }
                });
            }
        },

        // 標記點擊事件
        onMarkerClick(field) {
            // 直接使用 eventBus 打開案場詳細頁面
            const fieldTitle = field.name || `案場 ${field.id}`;
            // 從 view 獲取 orgId（view 是全局安裝的）
            const orgId = this.fieldScope?.orgId || this.fieldScope?.selectedOrgId;
            
            eventBus.emit('view:open', {
                path: '/field/FieldDetail',
                title: fieldTitle,
                options: {
                    fieldId: field.id,
                    orgId: orgId,
                    height: 420 // 指定高度為 420px（只用於地圖打開的案場視圖）
                }
            });
        },

        // 複製滑鼠位置
        copyMousePosition() {
            const text = `${this.mousePosition.lat}, ${this.mousePosition.lng}`;
            navigator.clipboard.writeText(text).then(() => {
                this.$Message.success('座標已複製到剪貼簿');
            }).catch(() => {
                this.$Message.error('複製失敗');
            });
        },

        // 處理來自 SystemSettings 的地圖類型切換請求
        handleMapTypeChangeRequest(type) {
            // 只處理 Google Maps 類型，Windy 類型由 WindyMap 組件處理
            if (['satellite', 'roadmap', 'hybrid', 'terrain'].includes(type)) {
                this.changeMapType(type);
            }
        },
        // 處理來自 SystemSettings 的地圖類型查詢請求
        handleMapTypeRequest() {
            this.broadcastMapType();
        },
        // 廣播當前地圖類型
        broadcastMapType() {
            eventBus.emit('ui:map-type-changed', this.currentMapType);
            eventBus.emit('ui:map-type-response', this.currentMapType);
        },
        // 地圖類型切換
        changeMapType(type) {
            // 首先更新當前地圖類型，觸發響應式更新
            this.currentMapType = type;
            
            // 保存到 localStorage
            localStorage.setItem('mapType', type);

            // 廣播地圖類型變化
            this.broadcastMapType();

            // 只處理 Google Maps 類型
            if (['satellite', 'roadmap', 'hybrid', 'terrain'].includes(type)) {
                this.$nextTick(() => {
                    const mapElement = this.$refs.mapComponent;
                    if (mapElement && mapElement.innerMap) {
                        try {
                            const mapTypes = {
                                satellite: window.google.maps.MapTypeId.SATELLITE,
                                roadmap: window.google.maps.MapTypeId.ROADMAP,
                                hybrid: window.google.maps.MapTypeId.HYBRID,
                                terrain: window.google.maps.MapTypeId.TERRAIN
                            };

                            mapElement.innerMap.setMapTypeId(mapTypes[type]);

                        } catch (error) {
                            console.warn('⚠️ 切換地圖類型時發生錯誤:', error);
                        }
                    }
                });
            }

        },

        // 載入並快取圖片到本地
        async cacheImageLocally(imageUrl, imageId) {
            // 檢查是否已經快取過
            if (this.imageCache.has(imageId)) {
                return this.imageCache.get(imageId);
            }

            try {
                // 使用 fetch 下載圖片
                const response = await fetch(imageUrl, {
                    mode: 'cors',
                    credentials: 'omit'
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                // 轉換為 Blob
                const blob = await response.blob();

                // 創建 Blob URL（更高效且不會超出 Data URL 大小限制）
                const blobUrl = URL.createObjectURL(blob);

                // 儲存到快取
                this.imageCache.set(imageId, blobUrl);

                return blobUrl;

            } catch (error) {
                console.warn(`⚠️ 圖片快取失敗 ${imageId}:`, error);
                // 快取失敗時返回原始 URL
                return imageUrl;
            }
        },

        // 清除圖片快取
        clearImageCache() {
            // 釋放 Blob URLs
            for (const [, blobUrl] of this.imageCache) {
                if (blobUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(blobUrl);
                }
            }

            this.imageCache.clear();
        },

        // 載入地圖圖塊（從 Firebase 載入案場圖塊覆蓋層）
        async loadMapTiles() {
            const mapElement = this.$refs.mapComponent;
            if (!mapElement || !mapElement.innerMap) {
                return;
            }

            const mapRef = collection(db, 'map');

            try {
                const querySnapshot = await getDocs(mapRef);
                const overlays = [];
                const bounds = new window.google.maps.LatLngBounds();

                // 首先，並行下載所有圖片到快取
                const cachePromises = [];
                const validTiles = [];

                querySnapshot.docs.forEach(doc => {
                    const tileData = doc.data();
                    if (tileData.southWestCoord && tileData.northEastCoord && tileData.imageUrl) {
                        validTiles.push({ doc, tileData });
                        // 開始並行快取圖片
                        cachePromises.push(this.cacheImageLocally(tileData.imageUrl, doc.id));
                    }
                });

                await Promise.allSettled(cachePromises); // 使用 allSettled 確保即使某些圖片失敗也繼續

                // 然後創建覆蓋層
                for (const { doc, tileData } of validTiles) {
                    const swLat = parseFloat(tileData.southWestCoord.lat);
                    const swLng = parseFloat(tileData.southWestCoord.lng);
                    const neLat = parseFloat(tileData.northEastCoord.lat);
                    const neLng = parseFloat(tileData.northEastCoord.lng);

                    if (isNaN(swLat) || isNaN(swLng) || isNaN(neLat) || isNaN(neLng)) {
                        console.warn(`⚠️ 圖塊 ${doc.id} 坐標無效，跳過處理`);
                        continue;
                    }

                    const tileBounds = new window.google.maps.LatLngBounds(
                        new window.google.maps.LatLng(swLat, swLng),
                        new window.google.maps.LatLng(neLat, neLng)
                    );

                    bounds.union(tileBounds);

                    try {
                        // 從快取獲取圖片 URL（圖片已經在之前並行快取了）
                        const imageId = doc.id;
                        const cachedImageUrl = this.imageCache.get(imageId) || tileData.imageUrl;

                        // 創建地面覆蓋層，使用快取的圖片 URL
                        const overlay = new window.google.maps.GroundOverlay(
                            cachedImageUrl, // 使用快取的圖片 URL
                            tileBounds,
                            {
                                clickable: false,
                                opacity: 0 // 初始透明，根據縮放級別控制顯示
                            }
                        );

                        overlay.setMap(mapElement.innerMap);
                        this.mapTiles.push(overlay);
                        overlays.push(overlay);

                    } catch (imageError) {
                        console.warn(`⚠️ 圖塊 ${doc.id} 覆蓋層創建失敗:`, imageError);
                    }
                }

                if (overlays.length > 0) {
                    // 設置縮放級別控制圖塊顯示
                    const updateTileVisibility = () => {
                        const zoom = mapElement.innerMap.getZoom();
                        overlays.forEach(overlay => {
                            if (zoom >= 17 && zoom <= 22) {
                                overlay.setOpacity(1); // 高縮放級別顯示圖塊
                            } else {
                                overlay.setOpacity(0); // 低縮放級別隱藏圖塊
                            }
                        });
                    };

                    // 監聽縮放變化
                    window.google.maps.event.addListener(mapElement.innerMap, 'zoom_changed', updateTileVisibility);

                    // 初始設置
                    updateTileVisibility();
                }

            } catch (error) {
                console.error('❌ 載入地圖圖塊時發生錯誤:', error);
            }
        },

        // 飛行到指定案場位置
        async flyToField(field) {
            const mapElement = this.$refs.mapComponent;
            if (!field || !mapElement?.innerMap || !this.isFieldSelected) {
                return;
            }

            let lat, lng;

            // 支持多種坐標格式
            if (field.location) {
                // 傳統格式：field.location.latitude, field.location.longitude
                lat = parseFloat(field.location.latitude);
                lng = parseFloat(field.location.longitude);
            } else {
                // 新格式：field.lat, field.lng 或 field.latitude, field.longitude
                lat = parseFloat(field.lat || field.latitude);
                lng = parseFloat(field.lng || field.longitude);
            }

            if (isNaN(lat) || isNaN(lng)) {
                console.error('❌ 無效的坐標:', field);
                return;
            }

            try {
                const targetLocation = new window.google.maps.LatLng(lat, lng);
                const targetZoom = 17;

                // 使用平滑動畫飛行到目標位置
                await this.smoothZoomMove(targetLocation, targetZoom);
            } catch (error) {
                console.error('❌ flyToField 執行失敗:', error);
            }
        },

        // 飛出到總覽視圖（顯示所有案場）
        async flyOut() {

            const mapElement = this.$refs.mapComponent;
            if (!mapElement || !mapElement.innerMap || this.isFieldSelected) {
                return;
            }

            // 添加防抖處理：如果正在進行flyOut，或者距離上次flyOut時間過短，則忽略
            const now = Date.now();
            if (this.isFlyingOut) {
                return;
            }

            // 如果距離上次調用時間過短，則延遲執行
            if (now - this.lastFlyOutTimestamp < 1000) {
                if (this.flyOutTimer) {
                    clearTimeout(this.flyOutTimer);
                }

                this.flyOutTimer = setTimeout(() => {
                    this.flyOut();
                }, 1000);

                return;
            }

            // 檢查是否有可見的標記
            if (this.visibleFields.length === 0) {
                // Web Components 版本中標記是響應式的，觸發更新
                await this.$nextTick();
            }

            // 設置標記位和時間戳記
            this.isFlyingOut = true;
            this.lastFlyOutTimestamp = now;

            try {
                // 計算所有可見案場的中心點
                let totalLat = 0;
                let totalLng = 0;
                let validFields = 0;
                let zoom = 8;

                this.visibleFields.forEach(field => {
                    if (field.lat && field.lng) {
                        totalLat += parseFloat(field.lat);
                        totalLng += parseFloat(field.lng);
                        validFields++;
                    }
                });

                // 如果沒有有效的案場位置，使用默認位置
                if (validFields === 0) {
                    totalLat = this.defaultLocation.lat;
                    totalLng = this.defaultLocation.lng;
                    validFields = 1;
                    zoom = 8;
                }

                // 計算中心點
                const centerLat = totalLat / validFields;
                const centerLng = totalLng / validFields;

                const currentCenter = mapElement.innerMap.getCenter();
                const currentZoom = mapElement.innerMap.getZoom();

                // 檢查是否需要飛行（如果位置差異很大或縮放級別不適合）
                const distance = calculateDistance(
                    currentCenter.lat(), currentCenter.lng(),
                    centerLat, centerLng
                );

                // 如果距離太近且縮放級別合適，則不需要飛行
                if (distance < 1000 && Math.abs(currentZoom - zoom) < 2) {
                    this.isFlyingOut = false;
                    return;
                }

                // 使用 Web Components 的地圖方法進行飛行
                const targetLocation = new window.google.maps.LatLng(centerLat, centerLng);

                // 使用平滑動畫飛行到目標位置
                await this.smoothZoomMove(targetLocation, zoom);

            } catch (error) {
                console.error('❌ flyOut 執行失敗:', error);
            } finally {
                // 確保無論成功與否都重置標記位
                this.isFlyingOut = false;
            }
        },

        async smoothZoomMove(targetLatLng, targetZoom) {
            const mapElement = this.$refs.mapComponent;
            if (!mapElement || !mapElement.innerMap) {
                return Promise.resolve();
            }

            const googleMap = mapElement.innerMap;
            const currentCenter = googleMap.getCenter();
            const currentZoom = googleMap.getZoom();

            // 取消當前正在進行的動畫
            if (this.currentMapAnimation) {
                cancelAnimationFrame(this.currentMapAnimation);
                this.currentMapAnimation = null;
            }

            // 如果起始位置與目標位置相同，則無需動畫
            if (
                this.isClose(currentCenter.lat(), targetLatLng.lat()) &&
                this.isClose(currentCenter.lng(), targetLatLng.lng()) &&
                Math.abs(currentZoom - targetZoom) < 0.5
            ) {
                return Promise.resolve();
            }

            // 使用 computed 屬性來決定是否啟用動畫
            if (!this.shouldEnableAnimation) {
                googleMap.setZoom(targetZoom);
                // googleMap.setCenter(targetLatLng);
                return Promise.resolve();
            }

            this.isMapAnimating = true;

            return new Promise(resolve => {
                // 動畫開始時間
                const startTime = performance.now();

                // 計算適合的動畫時長（毫秒）
                const fromLat = currentCenter.lat();
                const fromLng = currentCenter.lng();
                const toLat = targetLatLng.lat();
                const toLng = targetLatLng.lng();
                const fromZoom = currentZoom;
                const toZoom = targetZoom;
                const zoomDiff = Math.abs(fromZoom - toZoom);

                // 計算球面距離（米）
                const distance = calculateDistance(fromLat, fromLng, toLat, toLng);

                // 根據距離和縮放差異決定動畫時長
                const duration = calculateAnimationDuration(distance);

                // 是否需要使用中間縮放級別（遠距離移動時）
                const throughZoom = calculateIntermediateZoom(distance, zoomDiff);

                // 動畫函數 - 使用 requestAnimationFrame
                const animate = (currentTime) => {
                    // 計算已過時間和進度
                    const elapsed = currentTime - startTime;
                    const rawProgress = Math.min(elapsed / duration, 1);

                    // 應用緩動函數
                    const easeProgress = easeInOutCubic(rawProgress);

                    // 計算當前縮放級別
                    let currentAnimZoom;
                    if (throughZoom !== null) {
                        // 三段式動畫：先縮小，再移動，最後放大
                        if (rawProgress < 0.5) {
                            currentAnimZoom = fromZoom + (throughZoom - fromZoom) * easeInOutCubic(rawProgress * 2);
                        } else {
                            currentAnimZoom = throughZoom + (toZoom - throughZoom) * easeInOutCubic((rawProgress - 0.5) * 2);
                        }
                    } else {
                        // 直接動畫
                        currentAnimZoom = fromZoom + (toZoom - fromZoom) * easeProgress;
                    }

                    // 計算當前位置（最後一步直接使用目標位置）
                    let point;
                    if (rawProgress >= 0.999) {
                        point = { lat: toLat, lng: toLng };
                    } else {
                        // 使用工具函數計算球面線性插值
                        point = calculateIntermediatePoint(fromLat, fromLng, toLat, toLng, easeProgress);
                    }

                    // 更新地圖位置和縮放
                    googleMap.setZoom(currentAnimZoom);
                    googleMap.setCenter(new window.google.maps.LatLng(point.lat, point.lng));

                    // 繼續動畫或結束
                    if (rawProgress < 1) {
                        // 繼續下一幀
                        this.currentMapAnimation = requestAnimationFrame(animate);
                    } else {
                        // 動畫結束
                        this.isMapAnimating = false;
                        this.currentMapAnimation = null;

                        // 確保最終位置和縮放精確
                        googleMap.setZoom(Math.round(toZoom));
                        googleMap.setCenter(targetLatLng);

                        resolve();
                    }
                };

                // 開始動畫
                this.currentMapAnimation = requestAnimationFrame(animate);
            });
        },

        // 手動清除並重新載入圖塊（用於測試和調試）
        async reloadMapTiles() {
            // 清除現有覆蓋層
            if (this.mapTiles.length > 0) {
                this.mapTiles.forEach(overlay => {
                    overlay.setMap(null);
                });
                this.mapTiles = [];
            }

            // 可選：清除圖片快取（如果想要重新下載圖片）
            // this.clearImageCache();

            // 重新載入
            await this.loadMapTiles();
        },

        // 獲取快取統計資訊
        getCacheStats() {
            const stats = {
                cachedImages: this.imageCache.size,
                cacheEntries: Array.from(this.imageCache.keys())
            };
            return stats;
        },

        isClose(a, b, threshold = 0.0001) {
            return Math.abs(a - b) < threshold;
        }
    }
}
</script>

<style lang="stylus" scoped>
/* 自定義標記樣式 */
.custom-advanced-marker {
    position: relative;
    width: 40px;
    height: 56px;
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    user-select: none;
    font-family: 'Roboto', Arial, sans-serif;
    transform-origin: center bottom;
}

.name-label {
    position: absolute;
    top: -35px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.85);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s ease;
    z-index: 1002;
    max-width: 200px;
    text-align: center;
}

.marker-body {
    width: 20px;
    height: 20px;
    background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
    border: 2px solid #ffffff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    font-weight: bold;
    color: white;
    box-shadow:
        0 2px 8px rgba(25, 118, 210, 0.4),
        0 1px 4px rgba(0, 0, 0, 0.2);
    position: relative;
    z-index: 1001;
    transition: all 0.2s ease;
}

.marker-tail {
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 10px solid #1976d2;
    margin-top: -3px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
    z-index: 1000;
}

/* 風險等級的 pin 維持較大尺寸 */
.custom-advanced-marker.risk-high .marker-body,
.custom-advanced-marker.risk-medium .marker-body,
.custom-advanced-marker.risk-low .marker-body {
    width: 32px;
    height: 32px;
    border: 3px solid #ffffff;
    font-size: 12px;
}

.custom-advanced-marker.risk-high .marker-tail,
.custom-advanced-marker.risk-medium .marker-tail,
.custom-advanced-marker.risk-low .marker-tail {
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top-width: 16px;
    margin-top: -4px;
}

/* 懸停效果 */
.custom-advanced-marker:hover .marker-body {
    transform: scale(1.15);
    box-shadow:
        0 6px 16px rgba(25, 118, 210, 0.6),
        0 3px 8px rgba(0, 0, 0, 0.3);
}

.custom-advanced-marker:hover .name-label {
    opacity: 1;
}

/* 錯誤狀態標記 */
.custom-advanced-marker.status-error .marker-body {
    width: 32px;
    height: 32px;
    border: 3px solid #ffffff;
    font-size: 12px;
    background: linear-gradient(135deg, #ff5252 0%, #ff8a80 100%);
    box-shadow:
        0 4px 12px rgba(255, 82, 82, 0.5),
        0 2px 6px rgba(0, 0, 0, 0.2);
    animation: pulse-error 2s ease-in-out infinite;
}

.custom-advanced-marker.status-error .marker-tail {
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top-width: 16px;
    margin-top: -4px;
}

.custom-advanced-marker.status-error .marker-tail {
    border-top-color: #ff5252;
}

/* 錯誤徽章（警告圖示） */
.error-badge {
    position: absolute;
    top: -8px;
    right: -8px;
    width: 24px;
    height: 24px;
    background: #ff5252;
    color: white;
    border: 2px solid white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: bold;
    z-index: 1003;
    box-shadow: 0 2px 8px rgba(255, 82, 82, 0.6);
    animation: pulse-badge 1.5s ease-in-out infinite;
}

/* 脈衝動畫效果 */
@keyframes pulse-error {
    0%, 100% {
        transform: scale(1);
        filter: brightness(1);
    }

    50% {
        transform: scale(1.1);
        filter: brightness(1.2);
    }
}

@keyframes pulse-badge {
    0%, 100% {
        transform: scale(1);
        box-shadow: 0 2px 8px rgba(255, 82, 82, 0.6);
    }

    50% {
        transform: scale(1.15);
        box-shadow: 0 4px 16px rgba(255, 82, 82, 0.9);
    }
}

/* 錯誤狀態懸停效果 */
.custom-advanced-marker.status-error:hover .marker-body {
    transform: scale(1.25);
    box-shadow:
        0 6px 16px rgba(255, 82, 82, 0.8),
        0 3px 8px rgba(0, 0, 0, 0.3);
}

/* 風險等級 - 高風險（紅色） */
.custom-advanced-marker.risk-high .marker-body {
    background: linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%);
    box-shadow:
        0 4px 12px rgba(255, 77, 79, 0.5),
        0 2px 6px rgba(0, 0, 0, 0.2);
    animation: pulse-error 2s ease-in-out infinite;
}

.custom-advanced-marker.risk-high .marker-tail {
    border-top-color: #ff4d4f;
}

.custom-advanced-marker.risk-high:hover .marker-body {
    transform: scale(1.25);
    box-shadow:
        0 6px 16px rgba(255, 77, 79, 0.8),
        0 3px 8px rgba(0, 0, 0, 0.3);
}

/* 風險等級 - 中風險（黃色） */
.custom-advanced-marker.risk-medium .marker-body {
    background: linear-gradient(135deg, #faad14 0%, #ffc53d 100%);
    box-shadow:
        0 4px 12px rgba(250, 173, 20, 0.5),
        0 2px 6px rgba(0, 0, 0, 0.2);
}

.custom-advanced-marker.risk-medium .marker-tail {
    border-top-color: #faad14;
}

.custom-advanced-marker.risk-medium:hover .marker-body {
    transform: scale(1.15);
    box-shadow:
        0 6px 16px rgba(250, 173, 20, 0.6),
        0 3px 8px rgba(0, 0, 0, 0.3);
}

/* 風險等級 - 低風險（綠色） */
.custom-advanced-marker.risk-low .marker-body {
    background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
    box-shadow:
        0 4px 12px rgba(82, 196, 26, 0.4),
        0 2px 6px rgba(0, 0, 0, 0.2);
}

.custom-advanced-marker.risk-low .marker-tail {
    border-top-color: #52c41a;
}

.custom-advanced-marker.risk-low:hover .marker-body {
    transform: scale(1.15);
    box-shadow:
        0 6px 16px rgba(82, 196, 26, 0.6),
        0 3px 8px rgba(0, 0, 0, 0.3);
}

/* Google Maps 內建 InfoWindow 樣式 */
.info-window-content {
    position: relative;
}

/* 地圖載入樣式 */
.map-loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s ease, visibility 0.3s ease;
}

.map-loading-overlay.active {
    opacity: 1;
    visibility: visible;
}

.map-loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #1976d2;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

/* 地圖淡入淡出效果 */
.map-fade-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: black;
    z-index: 5;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.5s ease, visibility 0.5s ease;
}

.map-fade-overlay.active {
    opacity: 1;
    visibility: visible;
}

/* 地圖控制面板 */
.map-control-panel {
    position: fixed;
    top: 9px;
    right: 20px;
    z-index: 16;
}

.map-btn-list {
    display: flex;
    align-content: flex-start;
    flex-direction: row;
    gap: 4px;
    border-radius: 6px;
    padding: 4px;
}

.btn
    padding: 5px 7px;
    border: none;
    background: #cccccc66;
    backdrop-filter: blur(10px);
    color: #666;
    cursor: pointer;
    border-radius: 4px;
    font-size: 12px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    flex-direction: column;
    color: #333;
    gap: 4px;
    i
        font-size: 16px;
    span
        display: block;
        font-size: 12px;


.btn:hover {
    background: #eddddd;
    color: #1976d2;
}

.btn.active {
    background: #1976d2aa;
    backdrop-filter: blur(10px);
    color: white;
}

/* 滑鼠位置顯示 */
#mouse-position {
    display: none !important;
    position: fixed;
    bottom: 20px;
    right: 20px;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    z-index: 1000;
    display: flex;
    align-items: center;
    gap: 12px;
}

#mouse-position span {
    cursor: pointer;
    display: flex;
    align-items: center;
}

.animation-toggle {
    display: flex;
    align-items: center;
    gap: 4px;
    margin: 0;
    font-size: 11px;
}

/* 地圖容器過渡效果 */
#map {
    transition: opacity 0.4s ease, z-index 0s linear 0.2s, visibility 0.4s ease;
}

/* 當元素變為不可見時，立即改變 z-index */
#map[style*="opacity: 0"] {
    transition: opacity 0.4s ease, z-index 0s linear 0s, visibility 0.4s ease;
}

.map-center-info
    position: fixed
    right: 2px
    bottom: 2px
    background: rgba(0,0,0,0.85)
    color: #fff
    padding: 4px 6px
    border-radius: 6px
    font-size: 12px
    z-index: 900
    box-shadow: 0 2px 8px rgba(0,0,0,0.15)
    letter-spacing: 1px


</style>